import { z } from 'zod';
import { CalendarDate } from './project.js';
import { GoalPeriodKind, goalPeriod } from './performance-goal.js';
import { addCalendarDays } from './absence.js';
/**
 * Kỳ chứa `at`, cộng `offset` kỳ.
 *
 * Tên mang tiền tố `Okr` vì `period.ts` đã có một `shiftPeriod` khác cho kỳ
 * bảo trì (`2026-Q1` dạng chuỗi). Hai hàm cùng tên ở hai file rồi cùng được
 * `export *` ra một chỗ là một lỗi biên dịch — và nếu không, còn tệ hơn: một
 * chỗ gọi nhầm hàm kia và nhận `null` cho mọi kỳ.
 *
 * ⚠️ Nhảy bằng cách lấy **một ngày bên trong** kỳ đích rồi hỏi lại
 * `goalPeriod`, không cộng trừ 1 tháng / 3 tháng / 1 năm vào hai đầu. Cộng
 * trực tiếp sai ở tháng 1 (lùi về năm trước), ở tháng có 31 ngày, và ở năm
 * nhuận — ba chỗ chỉ lộ ra ở đúng vài ngày trong năm.
 */
export function shiftOkrPeriod(kind, offset, at) {
    const current = goalPeriod(kind, at);
    if (offset === 0)
        return { kind, ...current };
    const start = new Date(`${current.periodStart}T00:00:00Z`);
    const step = kind === 'month'
        ? Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + offset, 15)
        : kind === 'quarter'
            ? Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + offset * 3, 15)
            : Date.UTC(start.getUTCFullYear() + offset, 6, 15);
    return { kind, ...goalPeriod(kind, new Date(step)) };
}
/**
 * Hạn chốt OKR của một kỳ — APM-OKR2-d, kế hoạch §14.3.
 *
 * `ngày đầu kỳ + offset`. `offset` **âm** là chốt sớm (phải xong trước khi kỳ
 * bắt đầu), dương là chốt muộn (được phép chốt trong những ngày đầu kỳ).
 */
export function okrLockDate(period, offsetDays) {
    const start = new Date(`${period.periodStart}T00:00:00Z`);
    return new Date(start.getTime() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}
/**
 * Kỳ này còn **thêm / sửa / xoá** OKR được không — luật §14.2–§14.3.
 *
 * ## Hai điều kiện, và cả hai đều cần
 *
 * 1. Là **kỳ tương lai kế cận** hoặc **kỳ đang chạy**. Kỳ trước luôn khoá: một
 *    cái đích sửa được **sau khi biết kết quả** không còn là một cái đích. Kỳ
 *    xa hơn kỳ kế cận cũng khoá — đặt mục tiêu cho quý sau nữa là đoán, và nó
 *    lấp chỗ của kỳ kế cận trong mọi phép đếm.
 * 2. Hôm nay chưa quá **hạn chốt** của chính kỳ ấy (`okrLockDate`), tính
 *    **gồm cả** ngày hạn: hết hạn là hết ngày ấy theo lịch tổ chức.
 *
 * ## ⚠️ Kỳ đang chạy mở được — đó là nghĩa của offset dương
 *
 * Bản đầu chỉ nhận kỳ kế cận. Với offset dương ("được chốt thêm mấy ngày đầu
 * kỳ") thì sáng ngày đầu kỳ, kỳ ấy đã thành "đang chạy" và bị khoá trước cả
 * khi tới hạn — hạn +3 cho kỳ 01/11 khoá ngay từ 01/11 thay vì sau 04/11, và
 * offset 0 khoá luôn ngày 01/11 dù giao diện hứa "sửa được tới hết 01/11"
 * (lỗi production 02/11/2026, APM-OKRLOCK). Kỳ đang chạy với offset âm vẫn
 * khoá cả kỳ, vì hạn của nó nằm trước ngày đầu kỳ.
 *
 * ⚠️ `FinalizePersonalGoalCommand` và `ReportGoalValueCommand` **không** đi qua
 * cửa này. Một cửa chặn sửa mà chặn luôn việc chốt kỳ sẽ khoá cứng mọi mục
 * tiêu ở `active` mãi mãi — cùng loại lỗi đã ghi ở §13.3.
 *
 * @param at "hôm nay" của tổ chức (`localDayAnchor`) — hàm đọc ngày UTC của nó.
 */
export function canEditOkrPeriod(period, offsetDays, at) {
    const current = shiftOkrPeriod(period.kind, 0, at);
    const next = shiftOkrPeriod(period.kind, 1, at);
    if (period.periodStart !== current.periodStart && period.periodStart !== next.periodStart) {
        return false;
    }
    return at.toISOString().slice(0, 10) <= okrLockDate(period, offsetDays);
}
/**
 * Kỳ **đang chờ chốt** của một loại kỳ: kỳ đang chạy nếu nó còn trong hạn chốt
 * (offset ≥ 0, những ngày đầu kỳ), không thì kỳ kế cận.
 *
 * Đây là kỳ mà cảnh báo "chưa có OKR", ngày hạn hiện ở trang quản trị và
 * badge cùng nói tới. Nhìn cứng vào kỳ kế cận thì trong đúng những ngày ân hạn
 * — lúc gấp nhất — badge tắt và màn hình nói hạn của tháng sau.
 */
export function okrOpenPeriod(kind, offsetDays, at) {
    const current = shiftOkrPeriod(kind, 0, at);
    return canEditOkrPeriod(current, offsetDays, at) ? current : shiftOkrPeriod(kind, 1, at);
}
/**
 * Bao nhiêu ngày trước hạn chốt thì bắt đầu cảnh báo — APM-OKR2-f, §14.4.
 *
 * Ba con số cho ba loại kỳ, không phải một. Một con số chung thì hoặc quấy rầy
 * suốt với OKR năm (cảnh báo hai tháng liền), hoặc im lặng cho tới lúc quá
 * muộn với OKR tháng (còn hai ngày mới nhắc).
 */
export const OKR_WARN_DAYS = {
    month: 5,
    quarter: 10,
    year: 20,
};
/**
 * Có phải cảnh báo "kỳ tới chưa có OKR" không — §14.4.
 *
 * Cảnh báo khi **cả hai** đúng: kỳ tương lai kế cận chưa có mục tiêu nào, và
 * hôm nay đã vào quãng cận hạn. Thiếu vế thứ nhất thì badge sáng cả khi mọi
 * thứ đã xong; thiếu vế thứ hai thì nó sáng suốt cả kỳ và người ta học cách
 * bỏ qua nó — một badge luôn sáng không cảnh báo gì cả.
 *
 * ⚠️ Quá hạn rồi thì **thôi** cảnh báo: lúc ấy kỳ đã khoá, không ai làm gì
 * được nữa, và một lời nhắc không hành động được chỉ là tiếng ồn.
 */
export function okrNeedsAttention(kind, goalCountOpenPeriod, offsetDays, at) {
    if (goalCountOpenPeriod > 0)
        return false;
    const lock = okrLockDate(okrOpenPeriod(kind, offsetDays, at), offsetDays);
    const today = at.toISOString().slice(0, 10);
    if (today > lock)
        return false;
    return today >= okrWarnFrom(kind, offsetDays, at);
}
export function okrAttention(kind, goalCountOpenPeriod, offsetDays, at) {
    if (goalCountOpenPeriod > 0)
        return 'none';
    const today = at.toISOString().slice(0, 10);
    const lock = okrLockDate(okrOpenPeriod(kind, offsetDays, at), offsetDays);
    if (today > lock)
        return 'late';
    return today >= okrWarnFrom(kind, offsetDays, at) ? 'soon' : 'none';
}
/**
 * Ngày **bắt đầu nhắc** cho kỳ kế cận của một loại kỳ.
 *
 * Tách ra khỏi `okrNeedsAttention` để giao diện **nói ra được** con số ấy:
 * hai tab cùng hạn chốt 01/10 mà chỉ một tab sáng chấm trông y hệt một lỗi
 * hiển thị, cho tới khi có ai đó viết ra "cái kia sẽ nhắc từ 26/09" (người
 * dùng chốt giữ nguyên tiêu chí và giải thích nó, 2026-09-22).
 *
 * Một hàm, hai chỗ đọc — nếu không thì lời giải thích và điều kiện thật sẽ
 * trôi khỏi nhau, và màn hình sẽ hứa một ngày mà chấm không xuất hiện.
 */
export function okrWarnFrom(kind, offsetDays, at) {
    const lock = okrLockDate(okrOpenPeriod(kind, offsetDays, at), offsetDays);
    return new Date(new Date(`${lock}T00:00:00Z`).getTime() - OKR_WARN_DAYS[kind] * 86_400_000)
        .toISOString()
        .slice(0, 10);
}
/**
 * Hạn chốt của tổ chức, một số cho mỗi loại kỳ — APM-OKR2-d.
 *
 * ⚠️ Ba số riêng, không phải một. Một tổ chức chốt OKR năm từ tháng 12 năm
 * trước (`-30`) vẫn có thể cho chốt OKR tháng vào ngày mùng 3 (`+3`). Một con
 * số chung buộc hai nhịp rất khác nhau phải giống nhau.
 */
export const OkrLockOffsets = z.object({
    /**
     * Số ngày lệch so với ngày đầu kỳ. Âm = chốt sớm, dương = chốt muộn.
     *
     * Trần ±60: xa hơn thì "hạn chốt của kỳ sau" nhảy qua cả kỳ hiện tại và
     * không còn nghĩa gì với người đọc lịch.
     */
    month: z.number().int().min(-60).max(60),
    quarter: z.number().int().min(-60).max(60),
    year: z.number().int().min(-60).max(60),
});
/** Mặc định: chốt đúng ngày đầu kỳ. */
export const DEFAULT_OKR_LOCK_OFFSETS = { month: 0, quarter: 0, year: 0 };
/**
 * Số OKR **khuyến nghị tối đa** cho một kỳ — APM-OKR5, cấu hình cấp tổ chức.
 *
 * ## Con số ở đâu ra
 *
 * Google: 3–5 Objective mỗi kỳ, mỗi Objective ~3 Key Result. Rick Klau (2012)
 * đưa ra lập luận đếm được cho trần ấy: *"một quý có 12 tuần"* — mười hai mục
 * tiêu nghĩa là mỗi mục được một tuần, và một tuần không dịch chuyển được gì.
 * Chính ông từng đặt **7** mục trong một quý và "chưa bao giờ kiệt sức đến thế".
 *
 * App này đo mục tiêu ở mức **Key Result** (mỗi `personal_goal` là một chỉ số
 * có số), không ở mức Objective, nên trần thấp hơn con số 3–5 của Google:
 *
 * | Kỳ | Số tuần | Mặc định |
 * | --- | --- | --- |
 * | Tháng | ~4 | **2** |
 * | Quý | 12 | **3** |
 * | Năm | 52 | **3** |
 *
 * Năm bằng quý chứ không nhiều hơn, và đó là chủ đích: mục tiêu năm là mục tiêu
 * **định hướng**, không phải một danh sách dài hơn. Nhiều thời gian hơn không
 * làm một người theo dõi nổi nhiều đích hơn cùng lúc.
 *
 * ## ⚠️ Khuyến nghị, không phải cửa chặn
 *
 * Vượt trần thì giao diện **cảnh báo**, không khoá nút. Hơn nửa mục tiêu nên đi
 * từ dưới lên (deck Doerr 1999), và một cửa cứng ở đây sẽ chặn đúng những người
 * mà luật ấy muốn nghe. Trần **cứng** duy nhất là trần của gợi ý AI: model
 * không được đẩy một danh sách dài lên đầu người duyệt.
 */
export const OkrObjectiveLimits = z.object({
    /** 1–10. Dưới 1 thì không đặt được mục tiêu nào; trên 10 thì lời khuyên hết nghĩa. */
    month: z.number().int().min(1).max(10),
    quarter: z.number().int().min(1).max(10),
    year: z.number().int().min(1).max(10),
    /**
     * Số Key Result tối đa cho **một** Objective — APM-OKR6-e.
     *
     * Bốn theo Rick Klau (GV Startup Lab 2012): "ba tới bốn". Cái thứ năm gần như
     * luôn là một việc phải làm được viết thành một chỉ số, và nó kéo cả Objective
     * về thành một danh sách công việc.
     *
     * ⚠️ Con số này đếm **trong một** Objective, ba con số trên đếm **Objective**.
     * Một kỳ đầy đủ ở mặc định là 3 × 4 = 12 dòng — nhiều hơn trần cũ (3), và đó
     * là đúng: trần cũ đếm nhầm tầng.
     */
    krsPerObjective: z.number().int().min(1).max(10),
});
export const DEFAULT_OKR_OBJECTIVE_LIMITS = {
    month: 2,
    quarter: 3,
    year: 3,
    krsPerObjective: 4,
};
export const UpdateOkrObjectiveLimitsInput = OkrObjectiveLimits;
export const UpdateOkrLockOffsetsInput = OkrLockOffsets;
/**
 * Nhắc báo số KR tự do — người dùng yêu cầu 2026-10-01.
 *
 * KR tự do (`source = 'manual'`) chỉ có số khi **người** báo. Không ai nhắc thì
 * nó đứng ở con số của lần báo đầu suốt kỳ, và tới buổi đánh giá cuối kỳ cả hai
 * bên mới phát hiện ra "đạt / không đạt" đang dựa trên số của tháng trước.
 *
 * Mỗi `intervalPct` % quãng từ **lúc chốt OKR** tới **ngày làm việc cuối cùng
 * của kỳ** là một mốc nhắc; ngày làm việc cuối kỳ là mốc cuối — *không* đợi
 * đủ phần trăm (người dùng chốt: ngày cuối tháng có thể là ngày nghỉ, nên hạn
 * cuối là ngày làm việc cuối cùng).
 *
 * Một số chung cho cả ba loại kỳ (người dùng chốt 2026-10-01). Trần 10–50: dưới
 * 10% thì OKR tháng nhắc gần như mỗi ngày; trên 50% thì giữa kỳ không có mốc nào.
 */
export const OKR_REPORT_INTERVAL = { min: 10, max: 50, default: 25 };
export const OkrReportSettings = z.object({
    intervalPct: z.number().int().min(OKR_REPORT_INTERVAL.min).max(OKR_REPORT_INTERVAL.max),
});
export const DEFAULT_OKR_REPORT_SETTINGS = {
    intervalPct: OKR_REPORT_INTERVAL.default,
};
export const UpdateOkrReportSettingsInput = OkrReportSettings;
/**
 * Các mốc nhắc báo số của một kỳ, tăng dần — hàm thuần, dùng chung FE↔BE.
 *
 * - **Bắt đầu** = ngày chốt OKR (`okrLockDate`), nhưng không sớm hơn ngày đầu
 *   kỳ: hạn chốt sớm (offset âm) rơi vào kỳ trước, và kỳ chưa bắt đầu thì chưa
 *   có gì để báo.
 * - Mốc `k` = bắt đầu + `k × intervalPct %` quãng tới `lastWorkingDay`, **lùi
 *   tới ngày làm việc kế tiếp** nếu rơi vào ngày nghỉ (nhắc vào Chủ nhật là
 *   nhắc vào lúc không ai đọc).
 * - Mốc cuối luôn là `lastWorkingDay`. Mốc phần trăm nào trùng hoặc vượt nó thì
 *   bỏ — hai lời nhắc cùng một ngày là một lời nhắc thừa.
 *
 * @param lastWorkingDay ngày làm việc cuối cùng của kỳ theo lịch tổ chức
 *   (`previousWorkingDay(periodEnd)`) — tính ở API vì lịch nằm ở DB.
 * @param isWorkingDay cùng lịch ấy, để lùi mốc khỏi ngày nghỉ.
 */
export function okrReportCheckpoints(period, lockOffsetDays, intervalPct, lastWorkingDay, isWorkingDay = () => true) {
    const lock = okrLockDate(period, lockOffsetDays);
    const start = lock > period.periodStart ? lock : period.periodStart;
    if (start >= lastWorkingDay)
        return { start, checkpoints: [lastWorkingDay] };
    const span = (new Date(`${lastWorkingDay}T00:00:00Z`).getTime() -
        new Date(`${start}T00:00:00Z`).getTime()) /
        86_400_000;
    const checkpoints = [];
    for (let k = 1; k * intervalPct < 100; k += 1) {
        let day = addCalendarDays(start, Math.round((span * k * intervalPct) / 100));
        // Lùi khỏi ngày nghỉ — tối đa hai tuần, đủ cho kỳ nghỉ Tết dài nhất.
        for (let step = 0; step < 14 && !isWorkingDay(day) && day < lastWorkingDay; step += 1) {
            day = addCalendarDays(day, 1);
        }
        if (day >= lastWorkingDay)
            break;
        if (checkpoints.at(-1) !== day)
            checkpoints.push(day);
    }
    checkpoints.push(lastWorkingDay);
    return { start, checkpoints };
}
/**
 * KR này có đang **cần báo số** không — người dùng chốt 2026-10-01: nhắc khi
 * **không báo lần nào trong quãng vừa qua**.
 *
 * Tới mốc `k` (mốc mới nhất ≤ hôm nay), quãng vừa qua bắt đầu từ mốc `k − 1`
 * (hoặc từ lúc chốt OKR, với mốc đầu). Đã báo trong quãng ấy thì thôi: ai vừa
 * báo hôm qua mà hôm nay qua mốc không bị nhắc lại. Ở ngày làm việc cuối kỳ,
 * quãng là từ mốc phần trăm cuối — "chưa báo số một lần nữa".
 *
 * Chưa tới mốc đầu thì không nhắc gì: kỳ vừa chốt, chưa có gì để báo.
 *
 * @param lastReported ngày (theo lịch tổ chức) của lần báo gần nhất; `null` =
 *   chưa báo lần nào.
 */
export function okrReportDue(schedule, today, lastReported) {
    const reached = schedule.checkpoints.filter((day) => day <= today);
    if (reached.length === 0)
        return false;
    const windowStart = reached.length === 1 ? schedule.start : schedule.checkpoints[reached.length - 2];
    return lastReported === null || lastReported < windowStart;
}
/** Một KR tự do đang cần báo số — người dùng yêu cầu 2026-10-01. */
export const OkrReportDueItem = z.object({
    goalId: z.uuid(),
    title: z.string(),
    periodKind: GoalPeriodKind,
    periodStart: CalendarDate,
    /** Ngày làm việc cuối kỳ — hạn cuối để báo số của kỳ này. */
    lastWorkingDay: CalendarDate,
    /** Mốc nhắc vừa qua (mốc mới nhất ≤ hôm nay). */
    checkpoint: CalendarDate,
});
/**
 * KR tự do của **chính người hỏi** đang cần báo số — nguồn của chấm đỏ trên OKR
 * ở thanh điều hướng và của thông báo "Có X KR cần báo số".
 *
 * Bọc trong object, không trả mảng trần: "không có gì cần báo" là trạng thái
 * thường gặp nhất, và nó phải là một giá trị (cạm bẫy "endpoint trả null trần").
 */
export const OkrReportDue = z.object({
    count: z.number().int().nonnegative(),
    items: z.array(OkrReportDueItem),
});
/** Ngày lịch của một kỳ, dùng khi client gửi kỳ lên API. */
export const OkrPeriodInput = z.object({
    kind: GoalPeriodKind,
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
});
/**
 * Quản lý trực tiếp **mở khoá lại kỳ đang chạy** cho một cấp dưới — người dùng
 * yêu cầu 2026-10-01.
 *
 * Luật §14.2 khoá kỳ hiện tại để "đạt / không đạt" còn nghĩa. Nhưng nó cũng
 * khoá cả những lần sửa chính đáng: gõ nhầm một con số, đổi việc giữa kỳ, một
 * KR đo bằng chỉ số không còn áp dụng. Lối thoát là một **cuộc nói chuyện** với
 * quản lý — và cửa này là kết quả của cuộc nói chuyện ấy, không phải một nút
 * tự phục vụ.
 *
 * ## Ba giới hạn, và vì sao
 *
 * | Giới hạn | Vì sao |
 * | --- | --- |
 * | **chỉ kỳ đang chạy** | kỳ đã qua là đã biết kết quả — sửa đích lúc ấy là viết lại lịch sử; kỳ tới vốn đã mở |
 * | **từng người, từng loại kỳ** | mở cho cả nhóm là bỏ luật khoá cho cả nhóm |
 * | **tự khoá sau N ngày** | mở tay rồi quên khoá thì kỳ mở tới cuối kỳ — đúng lúc đã biết kết quả |
 *
 * Mở khoá chỉ nới **cửa thời gian**. Ai sửa được hàng nào (`assertCanEdit`)
 * giữ nguyên: nhân viên vẫn chỉ sửa / xoá mục chính mình tạo.
 */
export const OKR_UNLOCK_DAYS = { min: 1, max: 14, default: 3 };
export const UnlockOkrPeriodInput = z.object({
    days: z.number().int().min(OKR_UNLOCK_DAYS.min).max(OKR_UNLOCK_DAYS.max),
});
export const OkrPeriodUnlock = z.object({
    periodKind: GoalPeriodKind,
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    /** Thời điểm tự khoá lại — không bao giờ quá cuối ngày cuối kỳ. */
    expiresAt: z.iso.datetime(),
    unlockedByName: z.string(),
});
export const OkrUnlockStatus = z.object({
    /** Người đang xem là **quản lý trực tiếp** của chủ OKR — mở / khoá được. */
    canUnlock: z.boolean(),
    /** Chỉ những lần mở **còn hiệu lực** của kỳ đang chạy. */
    unlocks: z.array(OkrPeriodUnlock),
});
/**
 * Lần mở khoá này có mở được `period` không — dùng chung FE↔BE như
 * `canEditOkrPeriod`, để nút sửa và lệnh nói cùng một câu.
 *
 * @param at "hôm nay" của tổ chức (`localDayAnchor`) — để biết kỳ nào đang chạy.
 * @param now thời điểm thật — để so với `expiresAt`.
 */
export function isOkrUnlockActive(period, unlock, at, now) {
    if (!unlock)
        return false;
    if (unlock.periodKind !== period.kind || unlock.periodStart !== period.periodStart)
        return false;
    if (shiftOkrPeriod(period.kind, 0, at).periodStart !== period.periodStart)
        return false;
    return new Date(unlock.expiresAt).getTime() > now.getTime();
}
//# sourceMappingURL=okr-period.js.map